<script setup lang="ts" generic="T extends string | Option">
import type { Option } from './autocompleteTypes'
import { PhCircleNotch, PhMagnifyingGlass } from '@phosphor-icons/vue'
import { computed, nextTick, ref, watch } from 'vue'
import { onClickOutside } from '@vueuse/core'

interface Props {
  options: T[]
  label?: string
  errorMessage?: string
  placeholder?: string
  name?: string
  loading?: boolean
  searchIcon?: boolean
  enableFreeTextOption?: boolean
  freeTextOptionLabel?: (query: string) => string
  modelValue?: string
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  errorMessage: '',
  placeholder: '',
  name: '',
  loading: false,
  searchIcon: false,
  enableFreeTextOption: false,
  freeTextOptionLabel: (q: string) => `Search for "${q}"`,
})

interface FreetextItem {
  type: 'freetext'
  query: string
}

const emit = defineEmits<{
  'item-click': [value: T | FreetextItem]
  'update:modelValue': [value: string]
}>()

const query = ref(props.modelValue || '')
const isError = computed(() => Boolean(props.errorMessage))
const isOpen = ref(false)
const activeIndex = ref(-1)
const wrapperRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const listRef = ref<HTMLUListElement | null>(null)

onClickOutside(wrapperRef, () => {
  isOpen.value = false
})

const FREETEXT_OFFSET = computed(() => (props.enableFreeTextOption && query.value) ? 1 : 0)
const totalItems = computed(() => FREETEXT_OFFSET.value + props.options.length)
const shouldShowDropdown = computed(() => isOpen.value && (!!query.value || props.loading))

function handleInput(event: Event) {
  query.value = (event.target as HTMLInputElement).value
  activeIndex.value = -1
  isOpen.value = true
  emit('update:modelValue', query.value)
}

function handleFocus() {
  if (query.value) {
    isOpen.value = true
  }
}

function handleKeydownEscape() {
  isOpen.value = false
  activeIndex.value = -1
  inputRef.value?.blur()
}

function handleFreetextClick() {
  const item: FreetextItem = { type: 'freetext', query: query.value }
  emit('item-click', item as T | FreetextItem)
  isOpen.value = false
  activeIndex.value = -1
}

function handleItemClick(option: T) {
  emit('item-click', option)
  isOpen.value = false
  activeIndex.value = -1
}

function activateItem(index: number) {
  if (index < 0) {
    activeIndex.value = -1
    return
  }
  if (index >= totalItems.value) return
  activeIndex.value = index
  nextTick(() => {
    const items = listRef.value?.querySelectorAll('[data-item]')
    items?.[index]?.scrollIntoView({ block: 'nearest' })
  })
}

function handleKeydownArrowDown() {
  if (!shouldShowDropdown.value) {
    isOpen.value = true
    return
  }
  activateItem(activeIndex.value < totalItems.value - 1 ? activeIndex.value + 1 : 0)
}

function handleKeydownArrowUp() {
  if (!shouldShowDropdown.value) return
  activateItem(activeIndex.value > 0 ? activeIndex.value - 1 : totalItems.value - 1)
}

function handleKeydownEnter(e: KeyboardEvent) {
  if (!shouldShowDropdown.value) return

  e.preventDefault()
  if (activeIndex.value === -1) {
    if (props.enableFreeTextOption && query.value) {
      handleFreetextClick()
    }
    return
  }
  if (activeIndex.value === 0 && FREETEXT_OFFSET.value) {
    handleFreetextClick()
    return
  }
  const optionIndex = activeIndex.value - FREETEXT_OFFSET.value
  const option = props.options[optionIndex]
  if (option) handleItemClick(option)
}

function resolveLabel(option: T): string {
  if (typeof option === 'string') return option
  return option.name || String(option.id)
}

function isActive(index: number): boolean {
  return activeIndex.value === index
}

watch(query, (val) => {
  if (val) {
    isOpen.value = true
    activeIndex.value = -1
  }
})

watch(() => props.options, () => {
  activeIndex.value = -1
})
</script>

<template>
  <div ref="wrapperRef" class="autocomplete">
    <div class="autocomplete__wrapper">
      <div v-if="props.label" class="autocomplete__label">
        <slot name="label" :label="props.label">
          {{ props.label }}
        </slot>
      </div>
      <div class="autocomplete__input-container" :class="{ 'autocomplete__input-container--error': isError }">
        <div class="autocomplete__input-wrapper">
          <div v-if="props.searchIcon" class="autocomplete__input-icon autocomplete__input-icon--left">
            <PhMagnifyingGlass size="1.5rem" />
          </div>
          <input
            ref="inputRef"
            class="autocomplete__input"
            :class="{ 'autocomplete__input--with-left-icon': props.searchIcon }"
            :name="props.name"
            :value="query"
            :placeholder="props.placeholder"
            type="text"
            autocomplete="off"
            role="combobox"
            :aria-expanded="shouldShowDropdown"
            aria-haspopup="listbox"
            aria-autocomplete="list"
            @input="handleInput"
            @focus="handleFocus"
            @keydown.escape="handleKeydownEscape"
            @keydown.enter="handleKeydownEnter"
            @keydown.arrow-down.prevent="handleKeydownArrowDown"
            @keydown.arrow-up.prevent="handleKeydownArrowUp"
          />
        </div>
      </div>
      <div v-if="errorMessage" class="autocomplete__message" :class="{ 'autocomplete__message--error': isError }">
        {{ errorMessage }}
      </div>
    </div>
    <ul
      v-show="shouldShowDropdown"
      ref="listRef"
      class="autocomplete__options"
      role="listbox"
    >
      <div v-if="props.loading" class="autocomplete__loading">
        <slot name="loading">
          <PhCircleNotch size="24px" color="#0172AD" weight="bold" class="autocomplete__loading-spinner" />
        </slot>
      </div>
      <template v-else>
        <li
          v-if="props.enableFreeTextOption && query"
          data-item
          class="autocomplete__option autocomplete__option--freetext"
          :class="{ 'autocomplete__option--active': isActive(0) }"
          role="option"
          :aria-selected="isActive(0)"
          @click="handleFreetextClick"
          @mouseenter="activeIndex = 0"
          @mouseleave="activeIndex = -1"
        >
          <slot name="freetext-option" :query="query" :active="isActive(0)">
            <PhMagnifyingGlass size="1.25rem" class="autocomplete__option-icon" />
            <span>{{ props.freeTextOptionLabel(query) }}</span>
          </slot>
        </li>
        <li
          v-for="(option, idx) in props.options"
          :key="typeof option === 'string' ? option : option.id"
          data-item
          class="autocomplete__option"
          :class="{ 'autocomplete__option--active': isActive(idx + FREETEXT_OFFSET) }"
          role="option"
          :aria-selected="isActive(idx + FREETEXT_OFFSET)"
          @click="handleItemClick(option)"
          @mouseenter="activeIndex = idx + FREETEXT_OFFSET"
          @mouseleave="activeIndex = -1"
        >
          <slot
            name="option"
            :option="option"
            :active="isActive(idx + FREETEXT_OFFSET)"
          >
            {{ resolveLabel(option) }}
          </slot>
        </li>
        <div v-if="props.options.length === 0 && query !== ''" class="autocomplete__no-results">
          Keine Ergebnisse gefunden.
        </div>
      </template>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.autocomplete {
  display: flex;
  flex: 1 1 auto;
  position: relative;
  color: var(--neutral-80);

  &__wrapper {
    display: flex;
    flex-direction: column;
    align-items: start;
    width: 100%;
    gap: .5rem;
  }

  &__label {
    color: var(--neutral-80);
    margin: 0;
    padding: 0;
  }

  &__input-container {
    display: flex;
    height: 48px;
    align-items: center;
    align-self: stretch;
    border-radius: 8px;
    border: 1px solid #ced4da;
    background: #fff;
    position: relative;
    width: 100%;

    &:focus-within {
      border-color: #80bdff;
      outline: 0;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    &:hover {
      border-color: #80bdff;
    }

    &--error {
      border-color: #dc3545;
    }
  }

  &__input-wrapper {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    cursor: text;
    border: none;
    background: transparent;
    padding-right: 16px;
    padding-left: 16px;
    outline: none;
  }

  &__input {
    flex: 1 0 0;
    height: 100%;
    width: 100%;
    padding: 4px 0;
    border: none;
    background: transparent;
    outline: none;
    font-size: 16px;
    color: #495057;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;

    &::placeholder {
      color: #6c757d;
    }

    &--with-left-icon {
      padding-left: 8px;
    }
  }

  &__input-icon {
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    color: #6c757d;

    &--left {
      color: #3D4952;
    }
  }

  &__options {
    display: flex;
    flex-direction: column;
    padding: 8px 0;
    align-items: flex-start;
    border-radius: 8px;
    background: #f8f9fa;
    list-style: none;
    position: absolute;
    top: calc(100% + 4px);
    z-index: 999;
    width: 100%;
    overflow-y: auto;
    box-shadow: 0 15px 20px -10px rgba(0, 0, 0, 0.1);
    max-height: 360px;
  }

  &__option {

    border-bottom: 1px solid #dee2e6;
    width: 100%;

    &:hover {
      background: #e9ecef;
    }

    &--active {
      background: #e9ecef;
    }

    &--freetext {
      gap: 0;
      width: 100%;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 600;
      border-bottom: 1px solid #dee2e6;
      margin-bottom: 4px;
      padding-top: 12px;
      padding-bottom: 12px;
      padding-right: 24px;
      padding-left: 24px;
      color: #0172AD;
    }
  }

  &__option-icon {
    flex-shrink: 0;
    color: currentColor;
  }

  &__no-results {
    align-self: stretch;
    padding: 8px;
    color: #6c757d;
    font-style: italic;
    text-align: center;
  }

  &__loading {
    align-self: stretch;
    padding: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__loading-spinner {
    animation: autocomplete-spin 1s linear infinite;
  }

  &__message {
    padding: 0 16px;
    align-self: stretch;
    font-size: 14px;

    &--error {
      color: #dc3545;
    }
  }
}

@keyframes autocomplete-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
